// Iter 80 — Página /admin/health-checks · paranoia mode 06:00
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, AlertTriangle, CheckCircle2, Loader2, MinusCircle, RefreshCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  critical: { color: "rose", icon: AlertTriangle, label: "CRÍTICO" },
  warning: { color: "amber", icon: AlertTriangle, label: "WARNING" },
  ok: { color: "emerald", icon: CheckCircle2, label: "OK" },
  skipped: { color: "zinc", icon: MinusCircle, label: "SKIP" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.skipped;
  const Icon = meta.icon;
  const c = meta.color;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-${c}-500/15 text-${c}-300 border border-${c}-500/30`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function CheckRow({ check }) {
  return (
    <div className="grid grid-cols-12 gap-2 py-1.5 border-b border-zinc-800/50 last:border-0 text-xs items-center">
      <div className="col-span-3 font-mono text-zinc-300">{check.name}</div>
      <div className="col-span-1"><StatusBadge status={check.status} /></div>
      <div className="col-span-2 text-right text-zinc-500 font-mono">{check.duration_ms} ms</div>
      <div className="col-span-6 text-zinc-400">{check.details || "—"}</div>
    </div>
  );
}

export default function HealthChecks() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [latestRes, histRes] = await Promise.all([
        api.get("/admin/health-checks/latest"),
        api.get("/admin/health-checks?limit=30"),
      ]);
      setLatest(latestRes.data && latestRes.data.id ? latestRes.data : null);
      setHistory(histRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando health-checks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/health-checks/run");
      const c = data.criticals || 0;
      const w = data.warnings || 0;
      if (c > 0) toast.error(`${c} CRÍTICOS · ${w} warnings — revisa abajo`, { duration: 8000 });
      else if (w > 0) toast.warning(`${w} warnings (sin críticos)`, { duration: 5000 });
      else toast.success(`Todo OK · ${data.oks} checks pasados`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando healthcheck");
    } finally { setRunning(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" }) : "—";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="health-checks-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
            Health checks · Paranoia mode
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Suite diaria a las <b className="text-amber-300">06:00 (Madrid)</b>. Si hay críticos, te aviso por WhatsApp.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="hc-run-now-btn"
          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold flex items-center gap-2 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          {running ? "Ejecutando…" : "Run now"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : !latest ? (
        <div className="rounded-xl bg-zinc-900/40 border border-zinc-800 p-8 text-center">
          <Activity className="w-10 h-10 mx-auto text-zinc-600 mb-2" />
          <div className="text-zinc-400">Aún no se ha ejecutado ningún healthcheck. Pulsa <b>Run now</b>.</div>
        </div>
      ) : (
        <>
          {/* ─── Banner último resultado ─── */}
          <div className={`rounded-xl border-2 p-4 ${
            latest.criticals > 0 ? "bg-rose-500/10 border-rose-500/50"
              : latest.warnings > 0 ? "bg-amber-500/10 border-amber-500/40"
              : "bg-emerald-500/10 border-emerald-500/40"
          }`} data-testid="hc-latest-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Última ejecución</div>
                <div className="text-lg font-bold">
                  {latest.criticals > 0 ? "🚨 " : latest.warnings > 0 ? "⚠️ " : "✅ "}
                  {latest.criticals > 0
                    ? `${latest.criticals} CRÍTICOS · ${latest.warnings} warnings`
                    : latest.warnings > 0 ? `${latest.warnings} warnings (sin críticos)` : "Todo verde"}
                </div>
                <div className="text-xs text-zinc-400 mt-1">
                  {fmt(latest.ran_at)} · {latest.duration_ms} ms · {latest.total_checks} checks · trigger: <code>{latest.triggered_by}</code>
                </div>
              </div>
              <div className="flex gap-2">
                <Stat color="emerald" n={latest.oks} label="OK" />
                <Stat color="amber" n={latest.warnings} label="Warn" />
                <Stat color="rose" n={latest.criticals} label="Crít" />
                <Stat color="zinc" n={latest.skipped || 0} label="Skip" />
              </div>
            </div>
          </div>

          {/* ─── Detalle checks ─── */}
          <div className="rounded-xl bg-zinc-900/30 border border-zinc-800 overflow-hidden">
            <div className="px-3 py-2 bg-zinc-900/60 border-b border-zinc-800 text-xs text-zinc-400 font-bold flex items-center justify-between">
              <span>Checks ({latest.results?.length || 0})</span>
              <span className="text-[10px]">Ordenados por severidad</span>
            </div>
            <div className="px-3 py-1">
              {(latest.results || []).map((r) => (
                <CheckRow key={r.name} check={r} />
              ))}
            </div>
          </div>

          {/* ─── Historial ─── */}
          {history.length > 1 && (
            <div className="rounded-xl bg-zinc-900/30 border border-zinc-800 overflow-hidden">
              <div className="px-3 py-2 bg-zinc-900/60 border-b border-zinc-800 text-xs text-zinc-400 font-bold">
                Histórico (últimas {history.length})
              </div>
              <div className="divide-y divide-zinc-800/50">
                {history.map((h) => (
                  <div key={h.id} className="px-3 py-2 text-xs flex items-center gap-2 justify-between"
                       data-testid={`hc-hist-${h.id.slice(0, 8)}`}>
                    <div className="text-zinc-400 font-mono">{fmt(h.ran_at)}</div>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span className="text-zinc-500">{h.triggered_by}</span>
                      <span className="font-mono text-zinc-500">{h.duration_ms} ms</span>
                      <span className={`px-2 py-0.5 rounded font-bold ${
                        h.criticals > 0 ? "bg-rose-500/20 text-rose-300"
                          : h.warnings > 0 ? "bg-amber-500/20 text-amber-300"
                          : "bg-emerald-500/20 text-emerald-300"
                      }`}>
                        {h.criticals > 0 ? `${h.criticals}🔴` : h.warnings > 0 ? `${h.warnings}🟡` : "OK"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ color, n, label }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg bg-${color}-500/15 border border-${color}-500/30 text-center min-w-[60px]`}>
      <div className={`text-lg font-bold text-${color}-300 leading-none`}>{n}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}
